import { COLOR_POR_TIPO_EVENTO_TRABAJO, resolverColorSemantico } from "../lib/colorSemantico";
import { fechaHoyEnZonaSistema } from "../../shared/timezone";

export interface EventoCalendario {
  fecha: string;
  tipo: string;
}

const DIAS_SEMANA = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const NOMBRES_MES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];
// Tope defensivo: un rango "Personalizado" desmedido no debería renderizar
// decenas de grillas mensuales — evita listas/pantallas enormes.
const MAX_MESES = 3;

function iso(d: Date): string {
  return d.toISOString().substring(0, 10);
}

function sumarDias(d: Date, n: number): Date {
  return new Date(d.getTime() + n * 86_400_000);
}

function mesesEnRango(desde: string, hasta: string): { anio: number; mes: number }[] {
  const [anioDesde, mesDesde] = desde.split("-").map(Number);
  const [anioHasta, mesHasta] = hasta.split("-").map(Number);
  const meses: { anio: number; mes: number }[] = [];
  let anio = anioDesde;
  let mes = mesDesde - 1;
  while ((anio < anioHasta || (anio === anioHasta && mes <= mesHasta - 1)) && meses.length < MAX_MESES) {
    meses.push({ anio, mes });
    mes += 1;
    if (mes > 11) {
      mes = 0;
      anio += 1;
    }
  }
  return meses;
}

function semanasDelMes(anio: number, mes: number): Date[] {
  const primero = new Date(Date.UTC(anio, mes, 1));
  const ultimo = new Date(Date.UTC(anio, mes + 1, 0));
  const diaSemanaPrimero = primero.getUTCDay(); // 0 = domingo
  const inicioGrilla = sumarDias(primero, diaSemanaPrimero === 0 ? -6 : 1 - diaSemanaPrimero);
  const diaSemanaUltimo = ultimo.getUTCDay();
  const finGrilla = sumarDias(ultimo, diaSemanaUltimo === 0 ? 0 : 7 - diaSemanaUltimo);

  const dias: Date[] = [];
  let cursor = inicioGrilla;
  while (cursor.getTime() <= finGrilla.getTime()) {
    dias.push(cursor);
    cursor = sumarDias(cursor, 1);
  }
  return dias;
}

// Calendario mensual genérico: un punto de color por tipo de evento
// presente ese día (reutiliza resolverColorSemantico), click abre el
// detalle del día en el componente padre. Sin librería externa — no existía
// ningún componente de calendario en la app (confirmado por búsqueda
// exhaustiva), se construye acá desde cero pero con el mismo vocabulario
// visual del resto del ERP.
export function CalendarioMensual({
  desde,
  hasta,
  eventos,
  diaSeleccionado,
  onSeleccionarDia,
}: {
  desde: string;
  hasta: string;
  eventos: EventoCalendario[];
  diaSeleccionado?: string | null;
  onSeleccionarDia: (fecha: string) => void;
}) {
  const eventosPorDia = new Map<string, Set<string>>();
  for (const ev of eventos) {
    if (!eventosPorDia.has(ev.fecha)) eventosPorDia.set(ev.fecha, new Set());
    eventosPorDia.get(ev.fecha)!.add(ev.tipo);
  }

  const hoy = fechaHoyEnZonaSistema();
  const meses = mesesEnRango(desde, hasta);

  return (
    <div className="space-y-6">
      {meses.map(({ anio, mes }) => (
        <div key={`${anio}-${mes}`}>
          <h4 className="mb-2 text-sm font-semibold capitalize text-texto">
            {NOMBRES_MES[mes]} {anio}
          </h4>
          <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium text-texto-secundario">
            {DIAS_SEMANA.map((d) => (
              <div key={d} className="py-1">
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {semanasDelMes(anio, mes).map((dia) => {
              const fecha = iso(dia);
              const fueraDeMes = dia.getUTCMonth() !== mes;
              const tipos = Array.from(eventosPorDia.get(fecha) ?? []);
              const esHoy = fecha === hoy;
              const seleccionado = fecha === diaSeleccionado;
              return (
                <button
                  key={fecha}
                  type="button"
                  onClick={() => onSeleccionarDia(fecha)}
                  className={`flex h-16 flex-col items-center gap-1 rounded-lg border p-1 text-xs transition-colors ${
                    fueraDeMes ? "border-transparent text-texto-secundario/40" : "border-borde text-texto hover:bg-fondo"
                  } ${seleccionado ? "ring-2 ring-secundario" : ""} ${esHoy ? "bg-secundario/5 font-semibold" : ""}`}
                >
                  <span>{dia.getUTCDate()}</span>
                  <span className="flex flex-wrap justify-center gap-0.5">
                    {tipos.slice(0, 4).map((t) => (
                      <span
                        key={t}
                        className={`h-1.5 w-1.5 rounded-full ${resolverColorSemantico(COLOR_POR_TIPO_EVENTO_TRABAJO[t]).punto}`}
                      />
                    ))}
                    {tipos.length > 4 && <span className="text-[10px] text-texto-secundario">+{tipos.length - 4}</span>}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
